import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLanguage } from "../../contexts/LanguageContext";
import { Bell, Rocket, Gift, ArrowRight, Clock, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KickstarterSection = ({ onNotifyClick }) => {
  const { language } = useLanguage();
  const [subscriberCount, setSubscriberCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await axios.get(`${API}/kickstarter/subscribers/count`);
        setSubscriberCount(response.data.count || 0);
      } catch (error) {
        console.error("Failed to fetch subscriber count:", error);
      }
    };
    fetchCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="kickstarter" className="calli-section relative overflow-hidden" data-testid="kickstarter-section">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#05ce78] via-[#14c77a] to-[#0f9f5f]"></div>
      <div className="absolute inset-0 grid-pattern opacity-20"></div>
      <div className="glow-orb w-[600px] h-[600px] bg-white/10 top-0 right-0"></div>
      
      <div className="calli-container relative z-10">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10 space-y-4">
            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold uppercase tracking-wider bg-white/20 backdrop-blur-sm text-white border border-white/30">
              <Clock className="w-4 h-4" />
              {language === 'fr' ? 'Bientôt disponible' : 'Coming soon'}
            </span>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-black text-white">
              {language === 'fr' ? 'Lancement sur' : 'Launching on'}
            </h2>
            {/* Kickstarter Logo - Text styled */}
            <div className="flex justify-center items-center py-4">
              <span className="text-5xl sm:text-6xl md:text-7xl font-black text-white tracking-tight" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
                Kickstarter
              </span>
            </div>
            <p className="text-xl text-white/90">
              {language === 'fr' 
                ? 'La campagne de financement participatif arrive très bientôt !' 
                : 'The crowdfunding campaign is coming very soon!'}
            </p>

            {/* Subscriber Counter */}
            {subscriberCount > 0 && (
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full border border-white/30 animate-pulse" data-testid="subscriber-counter">
                <Users className="w-5 h-5 text-white" />
                <span className="text-white font-bold">
                  {language === 'fr' 
                    ? `Déjà ${subscriberCount} ${subscriberCount === 1 ? 'inscrit' : 'inscrits'} !`
                    : `Already ${subscriberCount} ${subscriberCount === 1 ? 'subscriber' : 'subscribers'}!`}
                </span>
              </div>
            )}
          </div>

          {/* Card */}
          <div className="bg-white rounded-[2rem] p-8 shadow-2xl">
            {/* Benefits */}
            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                <div className="p-3 bg-green-500 rounded-xl text-white">
                  <Gift className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">
                    {language === 'fr' ? 'Prix préférentiel Early Bird' : 'Early Bird Special Price'}
                  </h3>
                  <p className="text-gray-600">
                    {language === 'fr' 
                      ? 'Les premiers contributeurs bénéficieront d\'un prix exclusif bien inférieur au prix de vente final.'
                      : 'Early backers will benefit from an exclusive price well below the final retail price.'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-100">
                <div className="p-3 bg-orange-500 rounded-xl text-white">
                  <Rocket className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">
                    {language === 'fr' ? 'Accès prioritaire' : 'Priority Access'}
                  </h3>
                  <p className="text-gray-600">
                    {language === 'fr' 
                      ? 'Soyez parmi les premiers à recevoir Calli et participez à l\'aventure dès le début.'
                      : 'Be among the first to receive Calli and join the adventure from the start.'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="p-3 bg-blue-500 rounded-xl text-white">
                  <Bell className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">
                    {language === 'fr' ? 'Ne ratez pas le lancement' : 'Don\'t miss the launch'}
                  </h3>
                  <p className="text-gray-600">
                    {language === 'fr' 
                      ? 'Inscrivez-vous pour être notifié dès l\'ouverture de la campagne et profiter des meilleures offres.'
                      : 'Sign up to be notified when the campaign opens and get the best deals.'}
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center space-y-4 pt-6 border-t border-gray-200">
              <button
                onClick={onNotifyClick}
                className="w-full bg-gradient-to-r from-[#05ce78] to-[#0f9f5f] hover:from-[#04b86b] hover:to-[#0d8f55] text-white text-xl font-bold h-16 rounded-2xl shadow-lg shadow-green-500/30 transition-all duration-300 flex items-center justify-center gap-2"
                data-testid="kickstarter-notify-btn"
              >
                <Bell className="w-6 h-6" />
                {language === 'fr' ? 'Me notifier du lancement' : 'Notify me at launch'}
                <ArrowRight className="ml-1 w-5 h-5" />
              </button>

              <p className="text-sm text-gray-500">
                {language === 'fr' 
                  ? '🎁 Bonus exclusifs pour les premiers inscrits'
                  : '🎁 Exclusive bonuses for early subscribers'}
              </p>
            </div>
          </div>

          {/* Kickstarter badge */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="text-white font-black text-lg" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
                Kickstarter
              </span>
              <span className="text-white/80">•</span>
              <span className="text-white font-semibold">
                {language === 'fr' ? 'Campagne à venir' : 'Campaign coming soon'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KickstarterSection;
